import { useEffect, useRef, useState } from 'react'
import { TH_MONTHS_ABBR } from '../../lib/expense.js'

// Monthly เบิกใช้ per item — dependency-free SVG bars, sibling of
// ExpenseTrendChart (line) and BarChartH (horizontal). BARS, not a line: the
// buckets are discrete months of consumption, and a line between them implies
// a continuous rate that the data does not measure.
//
// The data MUST arrive gap-filled (lib/inventory.js#fillMonths). Xano returns
// only months that had movement, and a chart that skips empty months draws two
// non-adjacent months side by side — a silent lie about the trend.

// Colours come from the app's brand ramp (tailwind.config.js `brand` = Tailwind
// blue), not from a chart palette of their own — this page is blue, so the bars
// are blue. Hard-coded hex rather than Tailwind classes because SVG `fill` is an
// attribute; keep these in step with the config if the brand ever moves.
const BAR       = '#3b82f6'   // brand-500
const BAR_HOVER = '#1d4ed8'   // brand-700
const AVG       = '#1e40af'   // brand-800 — darker, so the reference line reads
const AVG_TEXT  = '#1e3a8a'   // brand-900   as "on top of" the bars, not as a series

const niceMax = (m) => {
  if (m <= 0) return 5
  const p = Math.pow(10, Math.floor(Math.log10(m)))
  return [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((x) => x * p).find((s) => s >= m) || 10 * p
}
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')

export default function StockUsageChart({ points = [], unit = '', loading = false, avg = 0 }) {
  const ref = useRef(null)
  const [w, setW] = useState(760)
  const [hover, setHover] = useState(null)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((e) => {
      const cw = e[0]?.contentRect?.width
      if (cw) setW(Math.max(320, Math.round(cw)))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const n = points.length
  const H = 210, padL = 44, padR = 14, padT = 14, padB = 30
  const innerW = w - padL - padR, innerH = H - padT - padB
  const max = niceMax(Math.max(0, ...points.map((p) => p.value)))
  const slot = n ? innerW / n : innerW
  const barW = Math.max(6, Math.min(46, slot * 0.62))
  const X = (i) => padL + slot * i + (slot - barW) / 2
  const Y = (v) => padT + (1 - (Number(v) || 0) / max) * innerH
  const rings = [0, 0.25, 0.5, 0.75, 1]

  // The last bucket is the month we are IN — it is incomplete by definition, so
  // it is drawn hollow. Otherwise every visit early in a month reads as a crash
  // in usage.
  const currentIdx = n - 1
  const avgY = avg > 0 ? Y(avg) : null

  const hp = hover != null ? points[hover] : null

  function onMove(e) {
    if (!ref.current || !n) return
    const rect = ref.current.getBoundingClientRect()
    const mx = (e.clientX - rect.left) * (w / (rect.width || w))
    const idx = Math.floor((mx - padL) / slot)
    setHover(idx >= 0 && idx < n ? idx : null)
  }

  if (loading) {
    return <div className="h-[210px] flex items-center justify-center text-[13px] text-slate-400">กำลังโหลดกราฟ…</div>
  }
  if (!n) {
    return <div className="h-[210px] flex items-center justify-center text-[13px] text-slate-400">ไม่มีข้อมูลการเบิกใช้ในช่วงนี้</div>
  }

  return (
    <div ref={ref} className="w-full relative" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      {hp && (
        <div className="pointer-events-none absolute z-10 -translate-x-1/2 rounded-lg bg-slate-900 px-2.5 py-1.5 text-[11.5px] text-white shadow-lg"
          style={{ left: `${((X(hover) + barW / 2) / w) * 100}%`, top: 0 }}>
          <div className="font-semibold tabular-nums">{fmt(hp.value)} {unit}</div>
          <div className="text-slate-300">
            {TH_MONTHS_ABBR[hp.month - 1]} {String(hp.year + 543).slice(2)}
            {hover === currentIdx && ' (ยังไม่จบเดือน)'}
          </div>
        </div>
      )}

      <svg viewBox={`0 0 ${w} ${H}`} className="w-full" style={{ height: H }}>
        {rings.map((r) => {
          const y = padT + (1 - r) * innerH
          return (
            <g key={r}>
              <line x1={padL} x2={w - padR} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
              <text x={padL - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill="#94a3b8" className="tabular-nums">
                {fmt(max * r)}
              </text>
            </g>
          )
        })}

        {/* Average line — the reorder conversation is "how much per month", so
            the mean belongs ON the chart, not only in a stat tile beside it. */}
        {avgY != null && (
          <g>
            <line x1={padL} x2={w - padR} y1={avgY} y2={avgY} stroke={AVG} strokeWidth="1.25" strokeDasharray="4 3" />
            <text x={w - padR} y={avgY - 4} textAnchor="end" fontSize="10" fill={AVG_TEXT}>
              เฉลี่ย {fmt(avg)}
            </text>
          </g>
        )}

        {points.map((p, i) => {
          const y = Y(p.value)
          const h = Math.max(p.value > 0 ? 2 : 0, padT + innerH - y)
          const partial = i === currentIdx
          return (
            <rect key={`${p.year}-${p.month}`} x={X(i)} y={y} width={barW} height={h} rx="3"
              fill={partial ? '#ffffff' : (hover === i ? BAR_HOVER : BAR)}
              stroke={partial ? BAR : 'none'}
              strokeWidth={partial ? 1.25 : 0}
              strokeDasharray={partial ? '3 2' : undefined} />
          )
        })}

        <line x1={padL} x2={w - padR} y1={padT + innerH} y2={padT + innerH} stroke="#e2e8f0" strokeWidth="1" />

        {points.map((p, i) => {
          // Thin the labels rather than overlapping them at 12 months on a
          // narrow column. The newest month always keeps its label.
          const step = Math.max(1, Math.ceil(n / (innerW < 420 ? 6 : 12)))
          if (i % step !== 0 && i !== n - 1) return null
          return (
            <text key={`l${p.year}-${p.month}`} x={X(i) + barW / 2} y={H - 10}
              textAnchor="middle" fontSize="10" fill="#94a3b8">
              {TH_MONTHS_ABBR[p.month - 1]}
              {p.month === 1 || i === 0 ? ` ${String(p.year + 543).slice(2)}` : ''}
            </text>
          )
        })}
      </svg>
    </div>
  )
}
